import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { cx } from './internal/cx'
import './Tabs.css'

export type TabItem = {
  value: string
  label: string
}

export type TabsProps = {
  value: string
  onChange: (value: string) => void
  tabs: TabItem[]
  ariaLabel?: string
}

/** Tablist with roving tabindex and arrow-key navigation. */
export function Tabs({ value, onChange, tabs, ariaLabel = 'Tabbladen' }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null)

  const move = (currentIndex: number, delta: number) => {
    const nextIndex = (currentIndex + delta + tabs.length) % tabs.length
    const next = tabs[nextIndex]
    if (!next) return
    onChange(next.value)
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    buttons?.[nextIndex]?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = tabs.findIndex((tab) => tab.value === value)
    if (currentIndex === -1) return
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      move(currentIndex, 1)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      move(currentIndex, -1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      move(0, 0)
    } else if (event.key === 'End') {
      event.preventDefault()
      move(tabs.length - 1, 0)
    }
  }

  return (
    <div className="ui-tabs" role="tablist" aria-label={ariaLabel} ref={listRef} onKeyDown={handleKeyDown}>
      {tabs.map((tab) => {
        const selected = tab.value === value
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={cx('ui-tabs__tab', selected && 'ui-tabs__tab--selected')}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
